/**
 * Переговорные компании (ЗД-14): справочник комнат для встреч и совещаний. Офисная комната — с адресом текстом,
 * комната на площадке — с проектом и объектом. Закрытую комнату переводят в архив: на неё будут ссылаться прошлые
 * встречи, поэтому удаления нет.
 */
import type { MeetingRoom } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { ApiError, api } from '../../lib/api';
import { projectQuery, projectsQuery } from '../../lib/projects';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast, unboundErrors } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

type Form = {
  name: string;
  location: string;
  projectId: string;
  objectId: string;
  capacity: string;
  hasScreen: boolean;
  hasVideo: boolean;
  hasBoard: boolean;
  videoUrl: string;
  note: string;
  isActive: boolean;
};

const FIELDS = ['name', 'location', 'projectId', 'objectId', 'capacity', 'videoUrl', 'note'] as const;

const EQUIPMENT: Array<[keyof Pick<MeetingRoom, 'hasScreen' | 'hasVideo' | 'hasBoard'>, string]> = [
  ['hasScreen', 'экран'],
  ['hasVideo', 'видеосвязь'],
  ['hasBoard', 'доска'],
];

/** Строка под названием комнаты: где она, сколько мест, что есть */
function roomLine(r: MeetingRoom): string {
  const where = r.projectId
    ? [r.projectCode ?? 'проект скрыт', r.objectName].filter(Boolean).join(', ')
    : (r.location ?? 'место не указано');
  const parts = [where];
  if (r.capacity) parts.push(`до ${r.capacity} чел.`);
  const eq = EQUIPMENT.filter(([k]) => r[k]).map(([, label]) => label);
  if (eq.length) parts.push(eq.join(', '));
  return parts.join(' · ');
}

function RoomDialog({ room, onClose }: { room: MeetingRoom | 'new'; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = room === 'new';
  const [f, setF] = useState<Form>({
    name: isNew ? '' : room.name,
    location: isNew ? '' : (room.location ?? ''),
    projectId: isNew ? '' : (room.projectId ?? ''),
    objectId: isNew ? '' : (room.objectId ?? ''),
    capacity: isNew || room.capacity == null ? '' : String(room.capacity),
    hasScreen: isNew ? false : room.hasScreen,
    hasVideo: isNew ? false : room.hasVideo,
    hasBoard: isNew ? false : room.hasBoard,
    videoUrl: isNew ? '' : (room.videoUrl ?? ''),
    note: isNew ? '' : (room.note ?? ''),
    isActive: isNew ? true : room.isActive,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  // Все видимые проекты: комната может стоять на проекте в архиве или в черновике
  const projects = useQuery(projectsQuery('all'));
  // Проект закрыт для правящего — сервер не отдаёт его код; тогда место комнаты не меняется (так же проверяет API)
  const placeLocked = !isNew && Boolean(room.projectId) && room.projectCode === null;
  const project = useQuery({
    ...projectQuery(f.projectId),
    enabled: Boolean(f.projectId) && !placeLocked,
  });
  // Новую комнату к проекту в архиве не привязать; свой проект комнаты остаётся в списке, даже если он в архиве
  const choices = (projects.data ?? []).filter(
    (p) => p.status !== 'archived' || (!isNew && p.id === room.projectId),
  );
  const ownProject =
    !isNew && room.projectId && !choices.some((p) => p.id === room.projectId)
      ? { id: room.projectId, label: room.projectCode ?? 'Проект скрыт' }
      : null;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        location: f.location || null,
        projectId: f.projectId || null,
        objectId: f.projectId ? f.objectId || null : null,
        capacity: f.capacity.trim() ? Number(f.capacity) : null,
        hasScreen: f.hasScreen,
        hasVideo: f.hasVideo,
        hasBoard: f.hasBoard,
        videoUrl: f.videoUrl || null,
        note: f.note || null,
        isActive: f.isActive,
      };
      return isNew
        ? api<MeetingRoom>('/meeting-rooms', { body })
        : api<MeetingRoom>(`/meeting-rooms/${room.id}`, { method: 'PUT', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meeting-rooms'] });
      toast(isNew ? 'Переговорная добавлена' : 'Сохранено');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    if (!f.name.trim()) {
      setErrors({ name: 'Укажите название' });
      return;
    }
    save.mutate();
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новая переговорная' : room.name}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="room-form" loading={save.isPending}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <form id="room-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Название" htmlFor="room-name" error={errors.name} className="sm:col-span-2">
          <Input
            id="room-name"
            value={f.name}
            onChange={set('name')}
            placeholder="Большая переговорная, 3 этаж"
            invalid={!!errors.name}
          />
        </Field>
        <Field
          label="Где находится"
          htmlFor="room-location"
          hint="Офис, этаж или адрес"
          error={errors.location}
          className="sm:col-span-2"
        >
          <Input id="room-location" value={f.location} onChange={set('location')} />
        </Field>
        <Field
          label="Проект"
          htmlFor="room-project"
          hint={
            placeLocked
              ? 'Проект закрыт для вас: место комнаты меняет тот, кто видит проект'
              : 'Для комнаты на площадке: прорабская, бытовка'
          }
          error={errors.projectId}
        >
          <Select
            id="room-project"
            value={f.projectId}
            disabled={placeLocked}
            onChange={(e) => setF((x) => ({ ...x, projectId: e.target.value, objectId: '' }))}
          >
            <option value="">Офис, не на площадке</option>
            {ownProject ? <option value={ownProject.id}>{ownProject.label}</option> : null}
            {choices.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
                {p.status === 'archived' ? ' (в архиве)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Объект проекта" htmlFor="room-object" error={errors.objectId}>
          <Select
            id="room-object"
            value={f.objectId}
            onChange={set('objectId')}
            disabled={!f.projectId || placeLocked}
          >
            <option value="">{f.projectId ? 'Весь проект' : 'Сначала выберите проект'}</option>
            {/* Объект комнаты, пока список объектов грузится или если проект закрыт, — остаётся как был */}
            {!isNew &&
            f.objectId &&
            f.objectId === room.objectId &&
            !project.data?.objects.some((o) => o.id === f.objectId) ? (
              <option value={f.objectId}>{room.objectName ?? 'Объект скрыт'}</option>
            ) : null}
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {[o.code, o.name].filter(Boolean).join(' ')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вместимость, человек" htmlFor="room-capacity" error={errors.capacity}>
          <Input
            id="room-capacity"
            value={f.capacity}
            onChange={(e) => setF((x) => ({ ...x, capacity: e.target.value.replace(/\D/g, '') }))}
            inputMode="numeric"
            maxLength={4}
            invalid={!!errors.capacity}
          />
        </Field>
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-[13px] font-medium text-ink">Оборудование</legend>
          <Checkbox
            label="Экран или проектор"
            checked={f.hasScreen}
            onChange={(e) => setF((x) => ({ ...x, hasScreen: e.target.checked }))}
          />
          <Checkbox
            label="Видеосвязь"
            checked={f.hasVideo}
            onChange={(e) => setF((x) => ({ ...x, hasVideo: e.target.checked }))}
          />
          <Checkbox
            label="Доска или флипчарт"
            checked={f.hasBoard}
            onChange={(e) => setF((x) => ({ ...x, hasBoard: e.target.checked }))}
          />
        </fieldset>
        <Field
          label="Ссылка на видеосвязь комнаты"
          htmlFor="room-video"
          hint="Постоянная ссылка Zoom, Teams или Meet — подставится во встречи в этой комнате"
          error={errors.videoUrl}
          className="sm:col-span-2"
        >
          <Input
            id="room-video"
            value={f.videoUrl}
            onChange={set('videoUrl')}
            placeholder="https://"
            inputMode="url"
            invalid={!!errors.videoUrl}
          />
        </Field>
        <Field
          label="Примечание"
          htmlFor="room-note"
          hint={
            placeLocked
              ? 'Скрыто: комната на проекте, закрытом для вас; при сохранении не меняется'
              : undefined
          }
          error={errors.note}
          className="sm:col-span-2"
        >
          <Textarea id="room-note" value={f.note} onChange={set('note')} rows={2} disabled={placeLocked} />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          label="Действует"
          description="Комнату в архиве нельзя выбрать для новых встреч; прошлые встречи её сохраняют"
          checked={f.isActive}
          onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))}
        />
        <ErrorBox
          className="sm:col-span-2"
          error={Object.keys(errors).length ? unboundErrors(errors, FIELDS) : save.error}
        />
      </form>
    </Dialog>
  );
}

export function MeetingRoomsPage() {
  const [showArchive, setShowArchive] = useState(false);
  const list = useQuery({
    queryKey: ['meeting-rooms', 'all'],
    queryFn: () => api<MeetingRoom[]>('/meeting-rooms', { query: { all: '1' } }),
  });
  const [editing, setEditing] = useState<MeetingRoom | 'new' | null>(null);
  const rooms = (list.data ?? []).filter((r) => showArchive || r.isActive);
  const archived = (list.data ?? []).filter((r) => !r.isActive).length;

  return (
    <>
      <Panel
        title="Переговорные"
        meta="комнаты для встреч и совещаний — в офисе и на площадках"
        actions={
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setEditing('new')}
          >
            Добавить переговорную
          </Button>
        }
        bodyClassName="p-0"
      >
        {list.isLoading ? (
          <div className="px-5">
            <Spinner />
          </div>
        ) : list.error ? (
          <div className="p-5">
            <ErrorBox error={list.error} />
          </div>
        ) : rooms.length ? (
          <ul>
            {rooms.map((r) => (
              <li
                key={r.id}
                className="flex flex-col gap-2 border-t border-line px-5 py-3.5 first:border-0 sm:flex-row sm:items-center sm:gap-4"
              >
                <button type="button" className="min-h-11 flex-1 text-left" onClick={() => setEditing(r)}>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink hover:underline">{r.name}</span>
                    {r.projectId ? <Badge tone="info">на площадке</Badge> : null}
                    {!r.isActive ? <Badge tone="neutral">в архиве</Badge> : null}
                  </span>
                  <span className="mt-0.5 block text-[13px] text-ink-2">{roomLine(r)}</span>
                </button>
                <HistoryButton table="meeting_rooms" rowId={r.id} title={r.name} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-5">
            <EmptyState title="Переговорных пока нет">
              Добавьте комнаты офиса и площадок — их можно будет выбирать при назначении встреч.
            </EmptyState>
          </div>
        )}
        {archived ? (
          <div className="border-t border-line px-5 py-2">
            <Button variant="ghost" size="md" onClick={() => setShowArchive((v) => !v)}>
              {showArchive ? 'Скрыть архив' : `Показать архив (${archived})`}
            </Button>
          </div>
        ) : null}
      </Panel>
      {editing ? (
        <RoomDialog
          key={editing === 'new' ? 'new' : editing.id}
          room={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
